import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProfile, updateProfile } from '../api/api';
import defaultProfile from "../assets/profile.png";
import Subscription from './Subscription';
import "./TechDashboard.css";
import api from '../Api';
import eventBus from '../eventBus';

const requestWorkGalleryDetails = async (workGalleryId, token, signal) => {
  const configuredTechnicianBase = process.env.REACT_APP_TECHNICIAN_API_BASE_URL;
  const technicianBaseUrl = new URL(configuredTechnicianBase || api.defaults.baseURL);
  const response = await fetch(
    new URL(`/api/web/work-gallery/${encodeURIComponent(workGalleryId)}`, technicianBaseUrl).toString(),
    {
      headers: { Authorization: 'Bearer ' + token },
      signal
    }
  );
  const responseText = await response.text();
  let responseData = {};
  if (responseText) {
    try {
      responseData = JSON.parse(responseText);
    } catch {
      throw new Error('The work gallery service returned an invalid response.');
    }
  }

  if (!response.ok || responseData.success === false || !responseData.data) {
    throw new Error(responseData.message || `Unable to load work details (HTTP ${response.status}).`);
  }

  return responseData.data;
};

const getWorkImageUrl = (image) => {
  const path = typeof image === 'string'
    ? image
    : image?.url || image?.image_url || image?.image || image?.path;
  if (!path || typeof path !== 'string') return '';
  try {
    return new URL(path, api.defaults.baseURL).toString();
  } catch {
    return path;
  }
};

const formatWorkDate = (date) => {
  if (!date) return '';
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime())
    ? date
    : parsedDate.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC'
    });
};

const getWorkDateInputValue = (date) => {
  if (!date) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  const parsedDate = new Date(date);
  return Number.isNaN(parsedDate.getTime()) ? '' : parsedDate.toISOString().slice(0, 10);
};

const TechDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [technicianId, setTechnicianId] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('technician_info') || '{}').technician_id || '';
    } catch {
      return '';
    }
  });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [viewCount, setViewCount] = useState(null);
  const [allCategories, setAllCategories] = useState([]); // Store all available categories for dropdown
  const [galleryForm, setGalleryForm] = useState({
    title: '',
    description: '',
    workDate: ''
  });
  const [workImages, setWorkImages] = useState([]);
  const [galleryPreviews, setGalleryPreviews] = useState([]);
  const [gallerySubmitting, setGallerySubmitting] = useState(false);
  const [galleryError, setGalleryError] = useState('');
  const [galleryMessage, setGalleryMessage] = useState('');
  const [galleryItems, setGalleryItems] = useState([]);
  const [galleryLoading, setGalleryLoading] = useState(false);
  const [galleryDeleting, setGalleryDeleting] = useState(false);
  const [selectedGalleryItem, setSelectedGalleryItem] = useState(null);
  const [galleryDetailLoading, setGalleryDetailLoading] = useState(false);
  const [isCreatingGalleryItem, setIsCreatingGalleryItem] = useState(false);
  const [isEditingGalleryItem, setIsEditingGalleryItem] = useState(false);

  // Profile state
  const [profileData, setProfileData] = useState({
    fullName: '',
    email: '',
    phoneNumber: '',
    address: '',
    city: '',
    zipCode: '',
    bloodGroup: '',
    serviceCategory: '',
    subCategory: '',
    childCategory: '',
    skills: [],
    profileImage: defaultProfile,
    about_us: '',
    categories: [], // Store full categories from API
    rating: {},
    jobsCompleted: null,
    responseTime: '',
    subscription_status: '',
    kyc_status: '',
    status: ''
  });

  const [formData, setFormData] = useState({ ...profileData });
  const [newSkill, setNewSkill] = useState('');
  const [profileImageFile, setProfileImageFile] = useState(null);

  const stats = {
    jobsCompleted: profileData.jobsCompleted,
    responseTime: profileData.responseTime,
    rating: Number(profileData.rating?.averageRating) || 0
  };

  // Fetch real profile data when component mounts
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
      if (!token) return;
      setLoading(true);
      setGalleryLoading(true);
      try {
        const resp = await getProfile(token);
        if (resp.success && resp.data) {
          const tech = resp.data;
          const id = tech.technician_id || tech._id || '';
          if (id) setTechnicianId(id);
          // Extract category information from categories array
          let mainCategory = '';
          let subCategory = '';
          let childCategory = '';
          
          if (tech.categories && tech.categories.length > 0) {
            const firstCategory = tech.categories[0];
            mainCategory = firstCategory.categoryName || '';
            
            // If subcategories exist, get the first one
            if (firstCategory.subcategories && firstCategory.subcategories.length > 0) {
              const firstSub = firstCategory.subcategories[0];
              subCategory = firstSub.subcategoryName || '';
              
              // If child categories exist, get the first one
              if (firstSub.childCategories && firstSub.childCategories.length > 0) {
                childCategory = firstSub.childCategories[0] || '';
              }
            }
          }
          
          const newProfileData = {
            fullName: tech.name || '',
            email: tech.email || '',
            phoneNumber: tech.mobile || '',
            address: tech.address || '',
            city: tech.city || '',
            zipCode: tech.zip_code || '',
            bloodGroup: tech.blood_group || '',
            serviceCategory: mainCategory,
            subCategory: subCategory,
            childCategory: childCategory,
            skills: tech.skills ? tech.skills.split(',').filter(s => s.trim()) : [],
            profileImage: tech.profile_pic || defaultProfile,
            about_us: tech.about_us || '',
            categories: tech.categories || [],
            rating: tech.rating || {},
            jobsCompleted: tech.jobs_completed ?? null,
            responseTime: tech.response_time || '',
            subscription_status: tech.subscription_status || '',
            kyc_status: tech.kyc_status || '',
            status: tech.status || ''
          };
          setProfileData(newProfileData);
          setFormData(newProfileData);
          setGalleryItems(Array.isArray(tech.work_gallery) ? tech.work_gallery : []);
          setGalleryError('');
          
          // Store categories for dropdown options
          if (tech.categories) {
            setAllCategories(tech.categories);
          }
        }
      } catch (err) {
        setApiError('Failed to load profile');
        setGalleryError('Unable to load your work gallery. Please try again.');
        console.error('Profile fetch error:', err);
      } finally {
        setLoading(false);
        setGalleryLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("technician_token") || localStorage.getItem("auth_token");

    if (!token) {
      navigate("/login");
      return;
    }

    const fetchViewCount = async () => {
      try {
        const response = await api.get("/api/web/technician/view-count", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const count = response?.data?.data?.viewCount;
        setViewCount(count == null || !Number.isFinite(Number(count)) ? null : Number(count));
      } catch (err) {
        console.error("Error fetching view count:", err);
        if (err.response?.status === 401) {
          localStorage.removeItem("technician_token");
          localStorage.removeItem("auth_token");
          navigate("/login");
        }
      }
    };

    fetchViewCount();
  }, [navigate]);

  // Keep formData in sync with profileData
  useEffect(() => {
    setFormData(profileData);
  }, [profileData]);

  useEffect(() => {
    const previews = workImages.map((file) => ({
      name: file.name,
      url: URL.createObjectURL(file)
    }));
    setGalleryPreviews(previews);
    return () => previews.forEach(({ url }) => URL.revokeObjectURL(url));
  }, [workImages]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'gallery', label: 'Work Gallery', icon: '📸' },
    { id: 'plans', label: 'Upgrade Plans', icon: '💎' }
  ];

  const handleTabChange = (tabId) => setActiveTab(tabId);

  const handleOpenGalleryForm = () => {
    setSelectedGalleryItem(null);
    setGalleryForm({ title: '', description: '', workDate: '' });
    setWorkImages([]);
    setIsEditingGalleryItem(false);
    setGalleryError('');
    setGalleryMessage('');
    setIsCreatingGalleryItem(true);
  };

  const handleCloseGalleryForm = () => {
    setGalleryForm({ title: '', description: '', workDate: '' });
    setWorkImages([]);
    setIsEditingGalleryItem(false);
    setGalleryError('');
    setGalleryMessage('');
    setIsCreatingGalleryItem(false);
  };

  const handleEditGalleryItem = () => {
    if (!selectedGalleryItem) return;
    setGalleryForm({
      title: selectedGalleryItem.title || '',
      description: selectedGalleryItem.description || '',
      workDate: getWorkDateInputValue(
        selectedGalleryItem.work_date || selectedGalleryItem.workDate || selectedGalleryItem.date
      )
    });
    setWorkImages([]);
    setGalleryError('');
    setGalleryMessage('');
    setIsEditingGalleryItem(true);
  };

  const handleOpenGalleryDetails = async (workGalleryId) => {
    const token = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
    if (!token) {
      setGalleryError('Your technician session has expired. Please sign in again.');
      navigate('/login?tab=tech', { state: { from: '/tech-dashboard' } });
      return;
    }

    setIsCreatingGalleryItem(false);
    setIsEditingGalleryItem(false);
    setSelectedGalleryItem(null);
    setGalleryError('');
    setGalleryDetailLoading(true);
    try {
      setSelectedGalleryItem(await requestWorkGalleryDetails(workGalleryId, token));
    } catch (error) {
      console.error('Work gallery details error:', error);
      setGalleryError(error.message || 'Unable to load work details.');
    } finally {
      setGalleryDetailLoading(false);
    }
  };

  const handleCloseGalleryDetails = () => {
    setSelectedGalleryItem(null);
    setIsEditingGalleryItem(false);
    setGalleryError('');
  };

  const handleDeleteGalleryItem = async () => {
    if (!selectedGalleryItem) return;
    const workGalleryId = selectedGalleryItem._id || selectedGalleryItem.id;
    if (!workGalleryId) {
      setGalleryError('Unable to identify this work gallery item.');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this work gallery item?')) return;

    const token = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
    if (!token) {
      setGalleryError('Your technician session has expired. Please sign in again.');
      navigate('/login?tab=tech', { state: { from: '/tech-dashboard' } });
      return;
    }

    setGalleryDeleting(true);
    setGalleryError('');
    setGalleryMessage('');
    try {
      const configuredTechnicianBase = process.env.REACT_APP_TECHNICIAN_API_BASE_URL;
      const technicianBaseUrl = new URL(configuredTechnicianBase || api.defaults.baseURL);
      const response = await fetch(
        new URL(`/api/web/technician-work-gallery/${encodeURIComponent(workGalleryId)}`, technicianBaseUrl).toString(),
        {
          method: 'DELETE',
          headers: { Authorization: 'Bearer ' + token }
        }
      );
      const responseText = await response.text();
      let responseData = {};
      if (responseText) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          throw new Error('The work gallery service returned an invalid response.');
        }
      }

      if (!response.ok || responseData.success === false) {
        throw new Error(responseData.message || `Unable to delete work from your gallery (HTTP ${response.status}).`);
      }

      setGalleryItems((currentItems) => currentItems.filter(
        (item) => (item._id || item.id) !== workGalleryId
      ));
      setSelectedGalleryItem(null);
      setIsEditingGalleryItem(false);
      setGalleryMessage(responseData.message || 'Work gallery item deleted.');
    } catch (error) {
      console.error('Work gallery delete error:', error);
      setGalleryError(error.message || 'Unable to delete work from your gallery.');
    } finally {
      setGalleryDeleting(false);
    }
  };
  
  const handleEditToggle = () => {
    if (isEditing) {
      // Save changes when exiting edit mode
      saveProfileToServer();
    }
    setIsEditing(!isEditing);
  };

  const saveProfileToServer = async () => {
    const token = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
    if (!token) return;

    setLoading(true);
    setApiError('');
    setStatusMessage('Updating profile...');

    const payload = new FormData();
    payload.append('name', formData.fullName);
    payload.append('email', formData.email);
    payload.append('mobile', formData.phoneNumber);
    payload.append('building_number', formData.address);
    payload.append('city', formData.city);
    payload.append('pincode', Number(formData.zipCode) || 0);
    payload.append('blood_group', formData.bloodGroup);
    
    // Handle categories - find the category ID from the categories array
    if (formData.serviceCategory) {
      const category = allCategories.find(c => c.categoryName === formData.serviceCategory);
      if (category) {
        payload.append('category_id', category._id);
        
        // Find subcategory
        if (formData.subCategory && category.subcategories) {
          const subCategory = category.subcategories.find(s => s.subcategoryName === formData.subCategory);
          if (subCategory) {
            payload.append('subcategory_id', subCategory._id);
          }
        }
      }
    }
    
    payload.append('skills', formData.skills.join(','));
    payload.append('about_us', formData.about_us);
    
    if (profileImageFile) {
      payload.append('profile_pic', profileImageFile);
    }

    try {
      const resp = await updateProfile(token, payload);
      if (resp.success) {
        setStatusMessage('Profile updated successfully!');
        setTimeout(() => setStatusMessage(''), 3000);
      }
    } catch (err) {
      setApiError(err.message || 'Update failed');
      console.error('Update error:', err);
      setTimeout(() => setApiError(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, profileImage: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleWorkUpload = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const invalidFile = selectedFiles.find((file) => !file.type.startsWith('image/'));
    if (invalidFile) {
      setGalleryError('Choose image files only.');
      event.target.value = '';
      return;
    }

    setGalleryError('');
    setGalleryMessage('');
    setWorkImages((currentFiles) => [...currentFiles, ...selectedFiles]);
    event.target.value = '';
  };

  const removeWorkImage = (indexToRemove) => {
    setWorkImages((currentFiles) => currentFiles.filter((_, index) => index !== indexToRemove));
  };

  const handleGalleryInputChange = (event) => {
    const { name, value } = event.target;
    setGalleryForm((current) => ({ ...current, [name]: value }));
  };

  const handleWorkGallerySubmit = async (event) => {
    event.preventDefault();
    setGalleryError('');
    setGalleryMessage('');

    const token = localStorage.getItem('technician_token') || localStorage.getItem('auth_token');
    if (!token) {
      setGalleryError('Your technician session has expired. Please sign in again.');
      navigate('/login?tab=tech', { state: { from: '/tech-dashboard' } });
      return;
    }

    if (!galleryForm.title.trim() || !galleryForm.description.trim() || !galleryForm.workDate ||
      (isCreatingGalleryItem && workImages.length === 0)) {
      setGalleryError(isCreatingGalleryItem
        ? 'Add a title, description, work date, and at least one image.'
        : 'Add a title, description, and work date.');
      return;
    }
    if (workImages.some((file) => !(file instanceof File))) {
      setGalleryError('One or more selected images are invalid. Please select them again.');
      return;
    }

    const payload = new FormData();
    payload.append('title', galleryForm.title.trim());
    payload.append('description', galleryForm.description.trim());
    payload.append('work_date', galleryForm.workDate);
    workImages.forEach((file) => payload.append('work_images', file));

    setGallerySubmitting(true);
    const uploadController = new AbortController();
    const uploadTimeoutId = window.setTimeout(() => uploadController.abort(), 60000);
    try {
      const configuredTechnicianBase = process.env.REACT_APP_TECHNICIAN_API_BASE_URL;
      const technicianBaseUrl = new URL(configuredTechnicianBase || api.defaults.baseURL);
      if (!configuredTechnicianBase && technicianBaseUrl.port === '5008') {
        technicianBaseUrl.port = '5008';
      }

      const response = await fetch(
        isEditingGalleryItem
          ? new URL(`/api/web/technician-work-gallery/${encodeURIComponent(selectedGalleryItem._id || selectedGalleryItem.id)}`, technicianBaseUrl).toString()
          : new URL('/api/web/work-gallery', technicianBaseUrl).toString(),
        {
          method: isEditingGalleryItem ? 'PUT' : 'POST',
          headers: { Authorization: 'Bearer ' + token },
          body: payload,
          signal: uploadController.signal
        }
      );
      const responseText = await response.text();
      let responseData = {};
      if (responseText) {
        try {
          responseData = JSON.parse(responseText);
        } catch {
          throw new Error('The work gallery service returned an invalid response.');
        }
      }

      if (!response.ok || responseData.success === false) {
        throw new Error(responseData.message || `Unable to ${isEditingGalleryItem ? 'update' : 'add'} work in your gallery (HTTP ${response.status}).`);
      }

      const updatedGalleryItem = isEditingGalleryItem ? responseData.data : null;
      if (isEditingGalleryItem && (!updatedGalleryItem || typeof updatedGalleryItem !== 'object')) {
        throw new Error(responseData.message || 'The work gallery service returned invalid updated work details.');
      }

      setGalleryMessage(responseData.message || (isEditingGalleryItem
        ? 'Work gallery item updated.'
        : 'Work added to your gallery.'));
      if (updatedGalleryItem) {
        const previousGalleryId = selectedGalleryItem._id || selectedGalleryItem.id;
        const updatedGalleryId = updatedGalleryItem._id || updatedGalleryItem.id;
        setSelectedGalleryItem(updatedGalleryItem);
        setGalleryItems((currentItems) => [
          updatedGalleryItem,
          ...currentItems.filter((item) => {
            const itemId = item._id || item.id;
            return itemId !== previousGalleryId && itemId !== updatedGalleryId;
          })
        ]);
      }
      setGalleryForm({ title: '', description: '', workDate: '' });
      setWorkImages([]);
      setIsCreatingGalleryItem(false);
      setIsEditingGalleryItem(false);
      setGalleryLoading(true);
      try {
        const refreshedProfile = await getProfile(token);
        if (!refreshedProfile.success || !refreshedProfile.data) {
          throw new Error(refreshedProfile.message || 'Unable to refresh your work gallery.');
        }
        const refreshedGalleryItems = Array.isArray(refreshedProfile.data.work_gallery)
          ? refreshedProfile.data.work_gallery
          : [];
        setGalleryItems(updatedGalleryItem
          ? [
            updatedGalleryItem,
            ...refreshedGalleryItems.filter((item) =>
              (item._id || item.id) !== (selectedGalleryItem._id || selectedGalleryItem.id) &&
              (item._id || item.id) !== (updatedGalleryItem._id || updatedGalleryItem.id)
            )
          ]
          : refreshedGalleryItems);
      } catch (refreshError) {
        console.error('Work gallery refresh error:', refreshError);
        setGalleryError(`Work was ${isEditingGalleryItem ? 'updated' : 'added'}, but the gallery could not be refreshed. Please try again.`);
      } finally {
        setGalleryLoading(false);
      }
    } catch (err) {
      console.error('Work gallery upload error:', err);
      const errorMessage = err.name === 'AbortError'
        ? 'The upload took too long. Please try again.'
        : err.response?.data?.message || err.message || 'Unable to upload work images. Please try again.';
      setGalleryError(errorMessage);
    } finally {
      window.clearTimeout(uploadTimeoutId);
      setGallerySubmitting(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skillToRemove) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(skill => skill !== skillToRemove)
    }));
  };

  const handleLogout = () => {
    localStorage.removeItem('technician_token');
    localStorage.removeItem('user_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('technician_info');
    localStorage.removeItem('user_info');
    eventBus.emit('userLoggedOut');
    navigate('/login');
  };

  const renderStars = (rating) => {
    const numRating = typeof rating === 'number' ? rating : parseFloat(rating) || 0;
    const full = Math.floor(numRating);
    const half = numRating % 1 >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    return (
      <>
        {[...Array(full)].map((_, i) => <span key={`full-${i}`} style={{color: '#FFD700'}}>★</span>)}
        {half && <span style={{color: '#FFD700'}}>½</span>}
        {[...Array(empty)].map((_, i) => <span key={`empty-${i}`} style={{color: '#ddd'}}>☆</span>)}
      </>
    );
  };

  return (
    <div className="techflowContainer">
      <div className="techflowWrapper">
        {/* Sidebar */}
        <aside className="techflowSidebar">
          <div className="techflowSidebarHeader">
            <h3>Technician Portal</h3>
          </div>
          <ul className="techflowSidebarNav">
            {menuItems.map(item => (
              <li
                key={item.id}
                className={`techflowSidebarItem ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => handleTabChange(item.id)}
              >
                <span className="techflowSidebarIcon">{item.icon}</span>
                <span className="techflowSidebarLabel">{item.label}</span>
              </li>
            ))}
          </ul>
          <div className="techflowSidebarFooter">
            <button className="techflowLogoutBtn" onClick={handleLogout}>
              🚪 Logout
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="techflowMain">
          {activeTab === 'dashboard' && (
            <div className="techflowTabContent">
              {/* Profile Header */}
              <div className="techflowProfileHeader">
                <div className="techflowProfileAvatar">
                  <img 
                    src={formData.profileImage || defaultProfile} 
                    alt="profile" 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = defaultProfile;
                    }}
                  />
                  {isEditing && (
                    <label className="techflowAvatarUpload">
                      <input type="file" accept="image/*" onChange={handleImageUpload} hidden />
                      <span>📷</span>
                    </label>
                  )}
                </div>
                <div className="techflowProfileInfo">
                  <h2>{formData.fullName || 'Technician'}</h2>
                  <div className="techflowRating">
                    <span className="techflowStars">{renderStars(stats.rating)}</span>
                    <span>{stats.rating > 0 ? stats.rating.toFixed(1) : '—'}</span>
                    <span>({profileData.rating?.totalReviews ?? '—'} reviews)</span>
                  </div>
                </div>
                <button
                  className={`techflowEditBtn ${isEditing ? 'techflowSaveBtn' : ''}`}
                  onClick={handleEditToggle}
                  disabled={loading}
                >
                  {isEditing ? '💾 Save Changes' : '✏️ Edit Profile'}
                </button>
              </div>

              {/* Stats Grid */}
              <div className="techflowStatsGrid">
                <div className="techflowStatCard">
                  <h3>{stats.rating > 0 ? stats.rating.toFixed(1) : '—'}</h3>
                  <p>Average Rating</p>
                </div>
                <div className="techflowStatCard">
                  <h3>{viewCount !== null ? viewCount : '---'}</h3>
                  <p>Profile Views</p>
                </div>
              </div>

              {/* Status Messages */}
              {statusMessage && <div className="techflowStatusMsg">{statusMessage}</div>}
              {apiError && <div className="techflowErrorMsg">{apiError}</div>}

              {/* ========== PROFILE DETAILS (READ‑ONLY VIEW) ========== */}
              {!isEditing && (
                <div className="techflowSection">
                  <h3>📋 Profile Information</h3>
                  <div className="techflowInfoGrid">
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Email:</span>
                      <span>{formData.email || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Phone:</span>
                      <span>{formData.phoneNumber || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Address:</span>
                      <span>{formData.address || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">City:</span>
                      <span>{formData.city || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Zip Code:</span>
                      <span>{formData.zipCode || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Blood Group:</span>
                      <span>{formData.bloodGroup || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Service Category:</span>
                      <span>{formData.serviceCategory || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Sub Category:</span>
                      <span>{formData.subCategory || '—'}</span>
                    </div>
                    <div className="techflowInfoItem">
                      <span className="techflowInfoLabel">Child Category:</span>
                      <span>{formData.childCategory || '—'}</span>
                    </div>
                    <div className="techflowInfoItem fullWidth">
                      <span className="techflowInfoLabel">About:</span>
                      <span>{formData.about_us || '—'}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Edit Form (only when editing) */}
              {isEditing && (
                <div className="techflowEditSection">
                  <h3>✏️ Edit Profile Details</h3>
                  <form className="techflowEditForm" onSubmit={(e) => e.preventDefault()}>
                    <div className="techflowFormRow">
                      <div className="techflowFormGroup">
                        <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder=" " />
                        <label>Full Name *</label>
                      </div>
                      <div className="techflowFormGroup">
                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder=" " />
                        <label>Email *</label>
                      </div>
                    </div>
                    <div className="techflowFormRow">
                      <div className="techflowFormGroup">
                        <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleInputChange} placeholder=" " />
                        <label>Phone *</label>
                      </div>
                      <div className="techflowFormGroup">
                        <input type="text" name="address" value={formData.address} onChange={handleInputChange} placeholder=" " />
                        <label>Address *</label>
                      </div>
                    </div>
                    <div className="techflowFormRow">
                      <div className="techflowFormGroup">
                        <input type="text" name="city" value={formData.city} onChange={handleInputChange} placeholder=" " />
                        <label>City *</label>
                      </div>
                      <div className="techflowFormGroup">
                        <input type="text" name="zipCode" value={formData.zipCode} onChange={handleInputChange} placeholder=" " maxLength="6" />
                        <label>Zip Code *</label>
                      </div>
                    </div>
                    <div className="techflowFormGroup">
                      <textarea 
                        name="about_us" 
                        value={formData.about_us} 
                        onChange={handleInputChange} 
                        rows="3" 
                        placeholder=" "
                      />
                      <label>About You</label>
                    </div>
                  </form>
                </div>
              )}

              {/* Skills Section (always visible) */}
              {/* <div className="techflowSection">
                <h3>🔧 Skills & Expertise</h3>
                <div className="techflowSkillsList">
                  {formData.skills && formData.skills.length > 0 ? (
                    formData.skills.map((skill, idx) => (
                      <span key={idx} className="techflowSkill">
                        {skill}
                        {isEditing && (
                          <button className="techflowRemoveSkill" onClick={() => removeSkill(skill)}>×</button>
                        )}
                      </span>
                    ))
                  ) : (
                    <span className="techflowNoData">No skills added yet</span>
                  )}
                </div>
                {isEditing && (
                  <div className="techflowAddSkill">
                    <input 
                      type="text" 
                      value={newSkill} 
                      onChange={(e) => setNewSkill(e.target.value)} 
                      placeholder="Add new skill"
                      onKeyPress={(e) => e.key === 'Enter' && addSkill()}
                    />
                    <button onClick={addSkill}>+ Add</button>
                  </div>
                )}
              </div> */}

            </div>
          )}

          {activeTab === 'gallery' && (
            <div className="techflowTabContent techflowGalleryTab">
              <div className="techflowGalleryHeading">
                <div>
                  <span className="techflowGalleryEyebrow">Your portfolio</span>
                  <h2>
                    {isCreatingGalleryItem || isEditingGalleryItem
                      ? (isEditingGalleryItem ? 'Edit Work Gallery' : 'Add Work Gallery')
                      : selectedGalleryItem
                        ? selectedGalleryItem.title || 'Work Details'
                        : 'Work Gallery'}
                  </h2>
                  <p>
                    {isCreatingGalleryItem || isEditingGalleryItem
                      ? (isEditingGalleryItem
                        ? 'Update the details and photos for this project.'
                        : 'Share a completed project with your customers.')
                      : selectedGalleryItem
                        ? 'Project details and photos.'
                        : 'Show customers the projects you have completed.'}
                  </p>
                </div>
                <div className="techflowGalleryHeadingActions">
                {isCreatingGalleryItem || isEditingGalleryItem || selectedGalleryItem ? (
                  <button
                    type="button"
                    className="techflowGallerySecondaryButton"
                    onClick={isCreatingGalleryItem
                      ? handleCloseGalleryForm
                      : isEditingGalleryItem
                        ? () => setIsEditingGalleryItem(false)
                        : handleCloseGalleryDetails}
                    disabled={gallerySubmitting || galleryDeleting}
                  >
                    {isEditingGalleryItem ? 'Cancel editing' : 'Back to gallery'}
                  </button>
                ) : (
                  <button type="button" className="techflowGalleryAddButton" onClick={handleOpenGalleryForm}>
                    <span aria-hidden="true">＋</span> Add Work Gallery
                  </button>
                )}
                {selectedGalleryItem && !isEditingGalleryItem && !isCreatingGalleryItem && (
                  <>
                    <button
                      type="button"
                      className="techflowGalleryAddButton"
                      onClick={handleEditGalleryItem}
                      disabled={galleryDeleting}
                    >
                      Edit work
                    </button>
                    <button
                      type="button"
                      className="techflowGalleryDeleteButton"
                      onClick={handleDeleteGalleryItem}
                      disabled={galleryDeleting}
                    >
                      {galleryDeleting ? 'Deleting…' : 'Delete work'}
                    </button>
                  </>
                )}
                </div>
              </div>

              {galleryMessage && (
                <div className="techflowStatusMsg" role="status">{galleryMessage}</div>
              )}
              {galleryError && (
                <div className="techflowErrorMsg" role="alert">{galleryError}</div>
              )}

              {galleryLoading && (
                <div className="techflowGalleryFeedback" role="status">Loading your work gallery…</div>
              )}
              {galleryDetailLoading && (
                <div className="techflowGalleryFeedback" role="status">Loading work details…</div>
              )}
              {!technicianId && !galleryLoading && (
                <div className="techflowGalleryFeedback" role="alert">
                  We could not find your technician account. Please sign in again and reopen your gallery.
                </div>
              )}

              {(isCreatingGalleryItem || isEditingGalleryItem) && (
              <form className="techflowGalleryForm" onSubmit={handleWorkGallerySubmit}>
                <div className="techflowGalleryFields">
                  <label className="techflowGalleryField">
                    <span>Work title</span>
                    <input
                      type="text"
                      name="title"
                      value={galleryForm.title}
                      onChange={handleGalleryInputChange}
                      placeholder="e.g. AC Repair Work"
                      maxLength={120}
                      required
                    />
                  </label>
                  <label className="techflowGalleryField">
                    <span>Work date</span>
                    <input
                      type="date"
                      name="workDate"
                      value={galleryForm.workDate}
                      onChange={handleGalleryInputChange}
                      required
                    />
                  </label>
                  <label className="techflowGalleryField techflowGalleryDescription">
                    <span>Description</span>
                    <textarea
                      name="description"
                      value={galleryForm.description}
                      onChange={handleGalleryInputChange}
                      placeholder="Describe the work you completed"
                      rows={4}
                      maxLength={2000}
                      required
                    />
                  </label>
                </div>

                <div className="techflowGalleryImageSection">
                  <div className="techflowGalleryImageHeading">
                    <div>
                      <h3>{isEditingGalleryItem ? 'Update work photos' : 'Work photos'}</h3>
                      <p>
                        {isEditingGalleryItem
                          ? 'Select images to include with this update. Leave empty to keep the current photos.'
                          : 'Select one or more images to include with this project.'}
                      </p>
                    </div>
                    <span>{workImages.length} selected</span>
                  </div>
                  {isEditingGalleryItem && Array.isArray(selectedGalleryItem.work_images) &&
                    selectedGalleryItem.work_images.length > 0 && (
                    <div className="techflowGalleryMediaGrid">
                      {selectedGalleryItem.work_images.map((image, index) => {
                        const imageUrl = getWorkImageUrl(image);
                        return imageUrl ? (
                          <img
                            key={`${imageUrl}-${index}`}
                            src={imageUrl}
                            alt={`Current ${selectedGalleryItem.title || 'work'} project ${index + 1}`}
                          />
                        ) : null;
                      })}
                    </div>
                  )}
                  <label className="techflowGalleryDropzone">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleWorkUpload}
                    />
                    <span className="techflowGalleryUploadIcon" aria-hidden="true">＋</span>
                    <strong>Add project photos</strong>
                    <span>Choose image files from your device</span>
                  </label>
                  {galleryPreviews.length > 0 && (
                    <div className="techflowGalleryPreviewGrid">
                      {galleryPreviews.map((preview, index) => (
                        <div className="techflowGalleryPreview" key={`${preview.name}-${index}`}>
                          <img src={preview.url} alt={`Selected work ${index + 1}`} />
                          <span title={preview.name}>{preview.name}</span>
                          <button
                            type="button"
                            onClick={() => removeWorkImage(index)}
                            aria-label={`Remove ${preview.name}`}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="techflowGalleryFormActions">
                  <button
                    type="button"
                    className="techflowGallerySecondaryButton"
                    onClick={handleCloseGalleryForm}
                    disabled={gallerySubmitting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="techflowGallerySubmit"
                    disabled={gallerySubmitting}
                  >
                    {gallerySubmitting
                      ? (isEditingGalleryItem ? 'Updating work…' : 'Uploading work…')
                      : (isEditingGalleryItem ? 'Save changes' : 'Add to Work Gallery')}
                  </button>
                </div>
              </form>
              )}

              {selectedGalleryItem && !isEditingGalleryItem && !galleryDetailLoading && (
                <section className="techflowGalleryDetails">
                  <div className="techflowGalleryDetailsMeta">
                    {(selectedGalleryItem.work_date || selectedGalleryItem.workDate || selectedGalleryItem.date) && (
                      <time dateTime={selectedGalleryItem.work_date || selectedGalleryItem.workDate || selectedGalleryItem.date}>
                        {formatWorkDate(selectedGalleryItem.work_date || selectedGalleryItem.workDate || selectedGalleryItem.date)}
                      </time>
                    )}
                  </div>
                  <p className="techflowGalleryDetailsDescription">
                    {selectedGalleryItem.description || 'No description provided.'}
                  </p>
                  {selectedGalleryItem.rejection_reason && (
                    <div className="techflowGalleryRejection">
                      <strong>Review note</strong>
                      <p>{selectedGalleryItem.rejection_reason}</p>
                    </div>
                  )}
                  {Array.isArray(selectedGalleryItem.work_images) && selectedGalleryItem.work_images.length > 0 && (
                    <div className="techflowGalleryMediaGrid">
                      {selectedGalleryItem.work_images.map((image, index) => {
                        const imageUrl = getWorkImageUrl(image);
                        return imageUrl ? (
                          <img key={`${imageUrl}-${index}`} src={imageUrl} alt={`${selectedGalleryItem.title || 'Work'} ${index + 1}`} />
                        ) : null;
                      })}
                    </div>
                  )}
                  {Array.isArray(selectedGalleryItem.work_videos) && selectedGalleryItem.work_videos.length > 0 && (
                    <div className="techflowGalleryMediaGrid">
                      {selectedGalleryItem.work_videos.map((video, index) => {
                        const videoUrl = getWorkImageUrl(video);
                        return videoUrl ? (
                          <video key={`${videoUrl}-${index}`} src={videoUrl} controls preload="metadata" />
                        ) : null;
                      })}
                    </div>
                  )}
                </section>
              )}

              {!isCreatingGalleryItem && !selectedGalleryItem && !galleryDetailLoading && !galleryLoading && technicianId && galleryItems.length === 0 && (
                <div className="techflowGalleryEmpty">
                  <span className="techflowGalleryEmptyIcon" aria-hidden="true">▧</span>
                  <h3>No work added yet</h3>
                  <p>Add completed projects to show your experience to customers.</p>
                  <button type="button" className="techflowGalleryAddButton" onClick={handleOpenGalleryForm}>
                    <span aria-hidden="true">＋</span> Add your first work
                  </button>
                </div>
              )}

              {!isCreatingGalleryItem && !selectedGalleryItem && !galleryDetailLoading && !galleryLoading && galleryItems.length > 0 && (
                <div className="techflowGalleryGrid">
                  {galleryItems.map((item, index) => {
                    const images = Array.isArray(item.work_images)
                      ? item.work_images
                      : item.work_images ? [item.work_images] : [];
                    const date = item.work_date || item.workDate || item.date;
                    return (
                      <button
                        type="button"
                        className="techflowGalleryCard"
                        key={item._id || item.id || `${item.title || 'work'}-${index}`}
                        onClick={() => handleOpenGalleryDetails(item._id || item.id)}
                        disabled={!item._id && !item.id}
                      >
                        {images.length > 0 ? (
                          <div className="techflowGalleryCardImages">
                            {images.slice(0, 3).map((image, imageIndex) => {
                              const imageUrl = getWorkImageUrl(image);
                              return imageUrl ? (
                                <img
                                  key={`${imageUrl}-${imageIndex}`}
                                  src={imageUrl}
                                  alt={`${item.title || 'Work'} ${imageIndex + 1}`}
                                  loading="lazy"
                                />
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <div className="techflowGalleryCardPlaceholder" aria-hidden="true">▧</div>
                        )}
                        <div className="techflowGalleryCardContent">
                          <div className="techflowGalleryCardMeta">
                            {date && <time dateTime={date}>{formatWorkDate(date)}</time>}
                            {images.length > 0 && <span>{images.length} {images.length === 1 ? 'photo' : 'photos'}</span>}
                          </div>
                          <h3>{item.title || 'Untitled work'}</h3>
                          <p>{item.description || 'No description provided.'}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Upgrade Plans Tab */}
          {activeTab === 'plans' && (
            <div className="techflowTabContent">
              <Subscription/>
            </div>
          )}

          {/* Placeholder Tabs */}
        </main>
      </div>
    </div>
  );
};

export default TechDashboard;